'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import { useToast } from '@common/hooks';
import { cn } from '@core/utils';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { AuthInfiniteQueryOptions, AuthMutation } from '@/api';
import { ReviewCard } from '@/app/donation/_components/ReviewCard';
import { ReviewCardMenu } from '@/app/donation/_components/ReviewCardMenu';
import { ReviewDetailSessionPortal } from '@/app/donation/_components/ReviewDetailSessionPortal';
import type { ReviewFormData } from '@/app/donation/(private)/my/review/_components/ReviewForm';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DonationReviewEditModal } from './DonationReviewEditModal';
const PAGE_SIZE = 10;
export const DonationReviewList = () => {
  const $ = _c(62);
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [editTargetId, setEditTargetId] = useState(null);
  const [detailReviewId, setDetailReviewId] = useState();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...AuthInfiniteQueryOptions["get:/wello/v2/auth/home/gift-review/my"]({
        query: {
          page: 1,
          size: PAGE_SIZE
        }
      }),
      getNextPageParam: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(t0);
  const observerRef = useRef(null);
  let t1;
  let t2;
  if ($[1] !== fetchNextPage || $[2] !== hasNextPage || $[3] !== isFetchingNextPage) {
    t1 = () => {
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }, {
        rootMargin: "0px 0px 1000px 0px"
      });
      if (observerRef.current) {
        observer.observe(observerRef.current);
      }
      return () => observer.disconnect();
    };
    t2 = [fetchNextPage, hasNextPage, isFetchingNextPage];
    $[1] = fetchNextPage;
    $[2] = hasNextPage;
    $[3] = isFetchingNextPage;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  const totalCount = data?.pages[0]?.context?.totalCount ?? 0;
  const reviews = data?.pages.flatMap(_temp2) ?? [];
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = AuthMutation["delete:/wello/v2/auth/home/gift-review/{reviewId}"]();
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== queryClient || $[8] !== toast) {
    t4 = async () => {
      await queryClient.invalidateQueries();
      toast({
        message: "\uD6C4\uAE30\uAC00 \uC0AD\uC81C\uB418\uC5C8\uC5B4\uC694."
      });
      setDeleteTargetId(null);
    };
    $[7] = queryClient;
    $[8] = toast;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== toast) {
    t5 = () => {
      toast({
        type: "fail",
        message: "\uD6C4\uAE30 \uC0AD\uC81C\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694."
      });
    };
    $[10] = toast;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = {
      ...t3,
      onSuccess: t4,
      onError: t5
    };
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const {
    mutateAsync: deleteReview,
    isPending: isDeleting
  } = useMutation(t6);
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = AuthMutation["put:/wello/v2/auth/home/gift-review"]();
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== queryClient || $[17] !== toast) {
    t8 = async () => {
      await queryClient.invalidateQueries();
      toast({
        message: "\uD6C4\uAE30\uB97C \uC218\uC815\uD588\uC5B4\uC694."
      });
      setEditTargetId(null);
    };
    $[16] = queryClient;
    $[17] = toast;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== toast) {
    t9 = () => {
      toast({
        type: "fail",
        message: "\uD6C4\uAE30 \uC218\uC815\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694."
      });
    };
    $[19] = toast;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = {
      ...t7,
      onSuccess: t8,
      onError: t9
    };
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const {
    mutateAsync: updateReview,
    isPending: isUpdating
  } = useMutation(t10);
  let t11;
  if ($[24] !== updateReview) {
    t11 = (reviewId, data_0) => {
      updateReview({
        body: {
          rating: data_0.rating,
          fileUrlList: data_0.imageUrlList,
          contents: data_0.contents,
          giftOrderProductReviewId: reviewId
        }
      });
    };
    $[24] = updateReview;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const handleEditSubmit = t11;
  let t12;
  if ($[26] !== totalCount) {
    t12 = <p className="px-20 py-8 font-500 text-Gray-black typo-body4">총 {totalCount}개</p>;
    $[26] = totalCount;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = totalCount === 0 ? <p className="py-80 text-center font-400 text-Gray-550 typo-body3">등록된 후기가 없어요.</p> : <><div>{reviews.map(item => <ReviewCard key={item.reviewId} rating={item.rating} createdAt={item.createdAt} productName={item.productName} contents={item.contents} imageUrlList={item.fileUrlList} reviewer={item.nickname} answer={item.answer} answerAt={item.answerAt} onImageClick={() => setDetailReviewId(item.reviewId)} actions={<ReviewCardMenu onDelete={() => setDeleteTargetId(item.reviewId)} onEdit={() => setEditTargetId(item.reviewId)} />} />)}</div>{hasNextPage && <div ref={observerRef} className="h-4 w-full" />}</>;
  const t14 = deleteTargetId !== null;
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setDeleteTargetId(null);
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="text-center"><p className={cn("pb-8 font-800 text-Gray-900 typo-h5", "mobile-tablet:font-700 mobile-tablet:typo-body2")}>작성한 후기를 삭제할까요?</p><p className={cn("font-400 text-Gray-700 typo-body2", "mobile-tablet:typo-body3")}>삭제하면 다시 확인할 수 없어요</p></div>;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = isDesktop ? "H56" : "H48";
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setDeleteTargetId(null);
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== isDeleting || $[32] !== t17) {
    t19 = <Button theme="sub-fill" dimensions={t17} className="flex-1" onClick={t18} disabled={isDeleting}>취소</Button>;
    $[31] = isDeleting;
    $[32] = t17;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const t20 = isDesktop ? "H56" : "H48";
  let t21;
  if ($[34] !== deleteReview || $[35] !== deleteTargetId) {
    t21 = () => {
      if (deleteTargetId !== null) {
        deleteReview({
          path: {
            reviewId: deleteTargetId
          }
        });
      }
    };
    $[34] = deleteReview;
    $[35] = deleteTargetId;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== isDeleting || $[38] !== t20 || $[39] !== t21) {
    t22 = <Button theme="primary-fill" dimensions={t20} className="flex-1" onClick={t21} disabled={isDeleting}>삭제</Button>;
    $[37] = isDeleting;
    $[38] = t20;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t19 || $[42] !== t22) {
    t23 = <div className="flex flex-col items-center gap-24">{t16}<div className="flex w-full gap-8">{t19}{t22}</div></div>;
    $[41] = t19;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t14 || $[45] !== t23) {
    t24 = <ResponsiveDialog opened={t14} onClose={t15} forceModal={true}>{t23}</ResponsiveDialog>;
    $[44] = t14;
    $[45] = t23;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setEditTargetId(null);
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== editTargetId || $[49] !== handleEditSubmit || $[50] !== isUpdating) {
    t26 = <DonationReviewEditModal reviewId={editTargetId} onClose={t25} onSubmit={handleEditSubmit} isSubmitting={isUpdating} />;
    $[48] = editTargetId;
    $[49] = handleEditSubmit;
    $[50] = isUpdating;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  const t27 = !!detailReviewId;
  let t28;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setDetailReviewId(undefined);
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== detailReviewId || $[54] !== t27) {
    t29 = <ReviewDetailSessionPortal open={t27} onClose={t28} reviewId={detailReviewId} />;
    $[53] = detailReviewId;
    $[54] = t27;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== t12 || $[57] !== t13 || $[58] !== t24 || $[59] !== t26 || $[60] !== t29) {
    t30 = <div>{t12}{t13}{t24}{t26}{t29}</div>;
    $[56] = t12;
    $[57] = t13;
    $[58] = t24;
    $[59] = t26;
    $[60] = t29;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  return t30;
};
function _temp(lastPage, allPages) {
  const contents = lastPage.context?.contents ?? [];
  if (contents.length < PAGE_SIZE) {
    return;
  }
  return allPages.length + 1;
}
function _temp2(page) {
  return page.context?.contents ?? [];
}